import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '@hookform/error-message'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { Button, Flex, Form, Input, Select, Skeleton, Typography, notification } from 'antd'
import { menuUrls } from 'src/app/shared/constants/menu.constants'

import Expirations from './Expirations'
import NavigationOptions from './NavigationOptions'
import PermissionSwitchers from './PermissionSwitcher'
import { generateValidationSchema } from './validation'
import { EXPIRATION_TYPE, contrastBinding, DEFAULT_GROUP_TYPE, DEFAULT_VIEW_ITEMS } from './constants'
import { useSelector, useDispatch } from 'react-redux'
import {
  getEmailsSetup,
  getModulesSetup,
  getPermissionsSetup,
  getPrimaryGroupSetup,
  setPrevDataEditUserSetup,
  setUpEmails,
  setUpModules,
  setUpPermissions,
  setUpPrimaryGroupTypes,
} from 'src/app/store/userRoleSetup'
import {
  fetchPermission,
  fetchPrimaryGroupType,
  fetchSubModuleItem,
  fetchUserNotificationType,
  getUserRoleById,
  putUpdateUserRole,
} from 'src/app/shared/services/UserRoleService'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import EmailSwitchers from './EmailSwitchers'
import { getActiveAll } from 'src/app/shared/utils'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { getUserProfile } from 'src/app/store/auth'
import { isViewPermissionOnly } from '..'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules, SUPER_ADMIN_ONLY_PERMISSIONS } from 'src/app/shared/models/roleAndPermission.model'
import { UserRoleIds } from 'src/app/shared/constants/app.constants'

const EditUserRole = () => {
  const params = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const userProfile = useSelector(getUserProfile)
  const { userPermissions } = useRolePermission()
  const isSystemAdmin: boolean = userPermissions[ESubModules.SPECIAL_PERMISSION]?.includes(EPermissions.FULL) || false
  const primaryGroupTypes = useSelector(getPrimaryGroupSetup)
  const [defaultGroupType, setDefaultGroupType] = useState<string>('')
  const [defaultPermission, setDefaultPermission] = useState<string>('')
  const permissions = useSelector(getPermissionsSetup)
  const emails = useSelector(getEmailsSetup)
  const modules = useSelector(getModulesSetup)
  const [loading, setLoading] = useState<boolean>(false)
  const [isFirstTimeLoadFinished, setIsFirstTimeLoadFinished] = useState<boolean>(false)

  const { id } = params

  const isSuperAdminRole = id?.toLowerCase() === UserRoleIds.SUPER_ADMIN.toLowerCase()
  const filteredPermissions = isSuperAdminRole
    ? permissions
    : permissions?.filter((permission: any) => !SUPER_ADMIN_ONLY_PERMISSIONS.includes(permission?.name))

  const getUserRoleInfo = async () => {
    if (id) {
      try {
        const primaryGroupTypeAPI = fetchPrimaryGroupType()

        const permissionAPI = fetchPermission()

        const [primaryGroupTypeRes, permissionRes] = await Promise.all([primaryGroupTypeAPI, permissionAPI])
        if (primaryGroupTypeRes?.data) {
          dispatch(setUpPrimaryGroupTypes(primaryGroupTypeRes?.data))
          setDefaultGroupType(
            primaryGroupTypeRes?.data?.filter((item: any) => item.codeName === DEFAULT_GROUP_TYPE)?.[0]?.id,
          )
        }
        if (permissionRes?.data) {
          dispatch(setUpPermissions(permissionRes?.data))
          setDefaultPermission(
            permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id,
          )
        }

        const res = await getUserRoleById(id)
        if (res?.data) {
          if (isViewPermissionOnly(userProfile?.defaultRole ?? '', res?.data?.userRoleName, isSystemAdmin)) {
            navigate(menuUrls.accessDenied)
            return
          }

          dispatch(setPrevDataEditUserSetup(res?.data))

          return {
            primaryGroupType: res?.data?.primaryGroupTypeId,
            roleName: res?.data?.userRoleName?.trim(),
            selectAllNavigations: res?.data?.navigationOptionIdResults?.length === getActiveAll(modules).length,
            navigationOptions: res?.data?.navigationOptionIdResults || [],
            selectAllPermissions: res?.data?.permissionIdResults?.length === permissionRes?.data?.length,
            permissions: res?.data?.permissionIdResults?.map((item: any) => ({ permissionId: item })) || [
              {
                permissionId: permissionRes?.data?.filter((item: any) => item.codeName === DEFAULT_VIEW_ITEMS)?.[0]?.id,
              },
            ],
            selectAllEmails: res?.data?.userNotificationTypeIdResults?.length === emails?.length,
            emails: res?.data?.userNotificationTypeIdResults?.map((item: any) => ({ emailId: item })) || [],
            expirations: res?.data?.expirationType || EXPIRATION_TYPE.DOES_NOT_EXPIRE,
            expirationMinutes:
              res?.data?.expirationType === EXPIRATION_TYPE.EXPIRE_X_MINUTES ? res?.data?.expirationMinutes : null,
            expirationDays:
              res?.data?.expirationType === EXPIRATION_TYPE.EXPIRE_X_DAYS ? res?.data?.expirationDays : null,
          }
        }
      } catch (error) {
        console.log('error', error)
        return {
          primaryGroupType: defaultGroupType,
          roleName: '',
          selectAllNavigations: false,
          selectAllPermissions: false,
          selectAllEmails: false,
          navigationOptions: [],
          permissions: [defaultPermission],
          emails: [],
          expirations: EXPIRATION_TYPE.DOES_NOT_EXPIRE,
          expirationMinutes: null,
          expirationDays: null,
        }
      }
    }

    navigate(menuUrls.userRoles)
  }

  const {
    control,
    formState: { errors, dirtyFields },
    resetField,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    trigger,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getUserRoleInfo(),
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const currentPrimaryGroupType = useWatch({
    control,
    name: 'primaryGroupType',
  })

  const currentExpirations = useWatch({
    control,
    name: 'expirations',
  })

  const navigationOptionsWatch = useWatch({
    control,
    name: 'navigationOptions',
  })

  const { fields: permissionFields, replace: replacePermission } = useFieldArray({
    control,
    name: 'permissions',
  })

  const {
    fields: emailFields,
    remove: removeEmail,
    replace: replaceEmail,
  } = useFieldArray({
    control,
    name: 'emails',
  })

  const handleCancel = () => {
    navigate(menuUrls.userRoles)
  }

  const onSubmit = async (data: any) => {
    const payload = {
      userRoleId: id,
      primaryGroupTypeId: data.primaryGroupType ?? [],
      userRoleName: data.roleName?.trim() ?? '',
      navigationOptionIds: data.navigationOptions ?? [],
      permissionIds: data.permissions?.map((item: any) => item?.permissionId) ?? [defaultPermission],
      userNotificationTypeIds: data.emails?.map((item: any) => item?.emailId) ?? [],
      expirationType: data.expirations,
      expirationMinutes: data.expirationMinutes || null,
      expirationDays: data.expirationDays || null,
    }

    setLoading(true)
    putUpdateUserRole(id as string, payload)
      .then((res: any) => {
        navigate(menuUrls.userRoles)

        notification.success({
          message: (
            <RichTextField
              richText={t(`SystemMessages:${SYSTEM_MESSAGE_IDS.UPDATE_ROLE_NAME_SUCCESS}`, {
                Role_Name: data.roleName?.trim(),
              })}
            />
          ),

          closeIcon: null,
        })
      })
      .catch((error) => {
        setLoading(false)
        if (error?.fieldError) {
          if (error?.error === 'PP80') {
            setError(contrastBinding[`${error?.fieldError}`] || '', {
              message: t(`SystemMessages:${error?.error}`, {
                Navigation_Options: error?.duplicatedNavigationOptions?.join(', '),
              }),
            })

            return
          }
          if (error?.error === 'PP91') {
            setError(contrastBinding[`${error?.fieldError}`] || '', {
              message: t(`SystemMessages:${error?.error}`),
            })

            return
          }
          setError(contrastBinding[`${error?.fieldError}`] || '', {
            message: t(`SystemMessages:${error.error}`),
          })
        } else {
          notification.error({
            message: t('common:error'),
            description: `${error.error}`,
            placement: 'topRight',
          })
        }
      })
  }

  const isRequiredExpireField = useCallback(() => {
    switch (currentExpirations) {
      case EXPIRATION_TYPE.EXPIRE_X_MINUTES:
        return !getValues('expirationMinutes')
      case EXPIRATION_TYPE.EXPIRE_X_DAYS:
        return !getValues('expirationDays')
      default:
        return false
    }
  }, [currentExpirations])

  const isSaveButtonDisabled =
    (!dirtyFields?.roleName &&
      !(dirtyFields?.navigationOptions instanceof Array
        ? dirtyFields?.navigationOptions?.includes(true)
        : dirtyFields?.navigationOptions) &&
      !dirtyFields?.expirations &&
      !dirtyFields?.expirationMinutes &&
      !dirtyFields?.expirationDays &&
      !dirtyFields?.selectAllEmails &&
      !dirtyFields?.selectAllPermissions &&
      !dirtyFields?.selectAllNavigations &&
      !dirtyFields?.emails?.map((email: any) => email?.emailId)?.includes(true) &&
      !dirtyFields?.permissions?.map((permission: any) => permission?.permissionId)?.includes(true)) ||
    !!Object.keys(errors)?.length ||
    isRequiredExpireField()

  useEffect(() => {
    if (navigationOptionsWatch?.length > 0) {
      clearErrors('navigationOptions')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationOptionsWatch])

  useEffect(() => {
    if (currentExpirations !== EXPIRATION_TYPE.EXPIRE_X_MINUTES) {
      resetField('expirationMinutes', { defaultValue: '' })
    }
    if (currentExpirations !== EXPIRATION_TYPE.EXPIRE_X_DAYS) {
      resetField('expirationDays', { defaultValue: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExpirations])

  useEffect(() => {
    if (!currentPrimaryGroupType) return

    Promise.all([
      fetchSubModuleItem(currentPrimaryGroupType !== defaultGroupType),
      fetchUserNotificationType(currentPrimaryGroupType === defaultGroupType ? 'bank' : 'client'),
    ])
      .then(([subModuleItemRes, userNotificationTypeRes]) => {
        if (subModuleItemRes?.data) {
          resetField('selectAllNavigations')

          if (navigationOptionsWatch?.length === getActiveAll(subModuleItemRes?.data).length) {
            setValue('selectAllNavigations', true)
          }
          dispatch(setUpModules(subModuleItemRes?.data))
        }

        if (userNotificationTypeRes?.data) {
          resetField('selectAllEmails')

          if (emailFields?.length === userNotificationTypeRes?.data?.length) {
            setValue('selectAllEmails', true)
          }
          dispatch(setUpEmails(userNotificationTypeRes?.data))
        }
      })
      .catch((err) => {
        console.log('Err >> ', err)
      })
      .finally(() => {
        setIsFirstTimeLoadFinished(true)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrimaryGroupType])

  if (!isFirstTimeLoadFinished) {
    return <Skeleton />
  }

  return (
    <Form
      name='edit-user-role-form'
      className='form-container edit-user-role-form'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      <Fragment>
        <Form.Item label={t('UserRoles:primaryGroupType.title')}>
          <Controller
            name='primaryGroupType'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                options={primaryGroupTypes?.map((item: any) => ({
                  ...item,
                  value: item?.id,
                  label: item?.name,
                }))}
                placeholder={t('UserRoles:primaryGroupType.placeholder')}
                onChange={(e) => {
                  field.onChange(e)

                  if (navigationOptionsWatch?.length > 0) {
                    setValue('navigationOptions', [])
                  }
                  if (emailFields?.length > 0) {
                    removeEmail()
                  }
                }}
                disabled
              />
            )}
          />
        </Form.Item>

        <Form.Item label={t('UserRoles:roleName.title')} className='isRequired'>
          <Controller
            name='roleName'
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                status={errors?.roleName ? 'error' : ''}
                placeholder={t('UserRoles:roleName.placeholder')}
                onChange={(e) => {
                  field.onChange(e?.target?.value?.trimStart())
                }}
                onBlur={(e) => {
                  let e1 = e
                  e1.target.value = e.target.value.trim()
                  field.onChange(e1)
                }}
                maxLength={40}
              />
            )}
          />

          <Typography className='error-message'>
            <ErrorMessage errors={errors} name='roleName' />
          </Typography>
        </Form.Item>

        <NavigationOptions
          control={control}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          navigationOptionsWatch={navigationOptionsWatch}
          trigger={trigger}
        />

        <PermissionSwitchers
          listSwitchers={filteredPermissions}
          title={t('UserRoles:permissions')}
          data={permissionFields}
          replace={replacePermission}
          control={control}
          setValue={setValue}
          trigger={trigger}
          name='permissions'
          isTurnOnFirst
          defaultPermission={defaultPermission}
        />

        <EmailSwitchers
          listSwitchers={emails}
          title={t('UserRoles:emails')}
          data={emailFields}
          replace={replaceEmail}
          control={control}
          setValue={setValue}
          trigger={trigger}
          name='emails'
        />

        <Expirations control={control} errors={errors} currentExpirations={currentExpirations} trigger={trigger} />
        <Flex className='button-group' justify='flex-end' gap={24} style={{ zIndex: 1000 }}>
          <Button className='cancel-button' onClick={handleCancel}>
            {t('UserRoles:cancel')}
          </Button>

          <Button
            className='save-button'
            type='primary'
            disabled={isSaveButtonDisabled}
            htmlType='button'
            onClick={handleSubmit((data) => onSubmit(data))}
            loading={loading}
            style={loading ? { width: 200 } : {}}
          >
            {t('UserRoles:saveUserRole')}
          </Button>
        </Flex>
      </Fragment>
    </Form>
  )
}

export default EditUserRole
